import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  PackageOpen, 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Upload,
  Download,
  Eye,
  DollarSign,
  Filter,
  ChevronDown
} from 'lucide-react';
import apiService from '../../services/api';
import axios from 'axios';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import Card from '../../components/UI/Card';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import CatalogItemPriceUpdateModal from '../../components/QuotationItems/CatalogItemPriceUpdateModal';
import { useSettings } from '../../contexts/SettingsContext';
import { QuotationItem } from '../../types';

const QuotationItemsPage: React.FC = () => {
  const [items, setItems] = useState<QuotationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPrimaryCategory, setFilterPrimaryCategory] = useState('');
  const [filterSubCategory, setFilterSubCategory] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<QuotationItem | null>(null);
  const [viewingItem, setViewingItem] = useState<QuotationItem | null>(null);
  const [priceUpdateItem, setPriceUpdateItem] = useState<QuotationItem | null>(null);
  
  // Dropdown data
  const [schedules, setSchedules] = useState<any[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [compressorTypes, setCompressorTypes] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  
  const [formData, setFormData] = useState({
    name: '',
    schedules: [] as string[],
    sections: [] as string[],
    parentCategory: '',
    subCategory: '',
    brand: '',
    model: '',
    sku: '',
    type: '',
    compressorType: '',
    countryOfOrigin: '',
    countryOfAssembly: '',
    details: '',
    description: '',
    specifications: '',
    coolingCapacity: '',
    serialNo: '',
    partNo: '',
    remarks: '',
    purchasePrice: '',
    units: [] as string[],
  });
  const [submitting, setSubmitting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const openingItemIdRef = useRef<string | null>(null);
  const unitsDropdownRef = useRef<HTMLDivElement | null>(null);
  const schedulesDropdownRef = useRef<HTMLDivElement | null>(null);
  const sectionsDropdownRef = useRef<HTMLDivElement | null>(null);
  const [unitsDropdownOpen, setUnitsDropdownOpen] = useState(false);
  const [schedulesDropdownOpen, setSchedulesDropdownOpen] = useState(false);
  const [sectionsDropdownOpen, setSectionsDropdownOpen] = useState(false);
  const { settings } = useSettings();

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (unitsDropdownOpen && unitsDropdownRef.current && !unitsDropdownRef.current.contains(target)) {
        setUnitsDropdownOpen(false);
      }
      if (schedulesDropdownOpen && schedulesDropdownRef.current && !schedulesDropdownRef.current.contains(target)) {
        setSchedulesDropdownOpen(false);
      }
      if (sectionsDropdownOpen && sectionsDropdownRef.current && !sectionsDropdownRef.current.contains(target)) {
        setSectionsDropdownOpen(false);
      }
    };
    if (unitsDropdownOpen || schedulesDropdownOpen || sectionsDropdownOpen) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [unitsDropdownOpen, schedulesDropdownOpen, sectionsDropdownOpen]);

  const itemToFormData = (item: any) => {
    const scheduleIds: string[] = Array.isArray(item?.schedules)
      ? item.schedules.map((s: any) => s?._id || s).filter(Boolean)
      : item?.schedule ? [item.schedule?._id || item.schedule].filter(Boolean) : [];
    const sectionIds: string[] = Array.isArray(item?.sections)
      ? item.sections.map((s: any) => s?._id || s).filter(Boolean)
      : item?.section ? [item.section?._id || item.section].filter(Boolean) : [];
    return {
      name: item?.name || '',
      schedules: scheduleIds,
      sections: sectionIds,
      parentCategory: item?.parentCategory?._id || item?.parentCategory || '',
      subCategory: item?.subCategory?._id || item?.subCategory || '',
      brand: item?.brand?._id || item?.brand || '',
      model: item?.model || '',
      sku: item?.sku || '',
      type: item?.type?._id || item?.type || '',
      compressorType: item?.compressorType?._id || item?.compressorType || '',
      countryOfOrigin: item?.countryOfOrigin?._id || item?.countryOfOrigin || '',
      countryOfAssembly: item?.countryOfAssembly?._id || item?.countryOfAssembly || '',
      details: item?.details || '',
      description: item?.description || '',
      specifications: item?.specifications || '',
      coolingCapacity: item?.coolingCapacity || '',
      serialNo: item?.serialNo || '',
      partNo: item?.partNo || '',
      remarks: item?.remarks || '',
      purchasePrice: item?.purchasePrice?.toString() || item?.unitPrice?.toString() || '',
      units: Array.isArray(item?.units)
        ? (item.units as any[]).map((u: any) => u?._id || u).filter(Boolean)
        : item?.unit ? [item.unit?._id || item.unit].filter(Boolean) : [],
    };
  };

  const fetchItems = async () => {
    try {
      const response = await apiService.getQuotationItems({ limit: 0 });
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error('Failed to fetch items');
    }
  };

  const fetchSchedules = async () => {
    try {
      const response = await apiService.getSchedules();
      setSchedules(response.data.schedules || response.data || []);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setSchedules([]);
    }
  };

  const fetchSections = async () => {
    try {
      const response = await apiService.getSections();
      setSections(response.data.sections || response.data || []);
    } catch (error) {
      console.error('Error fetching sections:', error);
      setSections([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiService.getCategories();
      setCategories(response.data.categories || response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await apiService.getBrands();
      setBrands(response.data.brands || response.data || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
      setBrands([]);
    }
  };

  const fetchTypes = async () => {
    try {
      const response = await apiService.getTypes();
      setTypes(response.data.types || response.data || []);
    } catch (error) {
      console.error('Error fetching types:', error);
      setTypes([]);
    }
  };

  const fetchCompressorTypes = async () => {
    try {
      const response = await apiService.getCompressorTypes();
      setCompressorTypes(response.data.compressorTypes || response.data || []);
    } catch (error) {
      console.error('Error fetching compressor types:', error);
      setCompressorTypes([]);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await apiService.getCountries();
      setCountries(response.data.countries || response.data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
      setCountries([]);
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await apiService.getUnits();
      setUnits(response.data.units || response.data || []);
    } catch (error) {
      console.error('Error fetching units:', error);
      setUnits([]);
    }
  };

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setImporting(true);
      const response = await apiService.importQuotationItemsFromExcel(file);
      const data = response.data;
      
      let message = `Successfully imported ${data.importedCount} item(s)`;
      if (data.skippedCount > 0) {
        message += ` (${data.skippedCount} skipped due to duplicates)`;
      }
      if (data.errors && data.errors.length > 0) {
        message += `. ${data.errors.length} row(s) had errors.`;
      }
      
      toast.success(message);
      fetchItems();
    } catch (error: any) {
      console.error('Error importing items from Excel:', error);
      const errorMessage = error.response?.data?.error || 'Failed to import items from Excel';
      const errors = error.response?.data?.errors;
      
      if (errors && Array.isArray(errors) && errors.length > 0) {
        toast.error(`${errorMessage}\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? `\n... and ${errors.length - 5} more` : ''}`);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownloadSample = async () => {
    try {
      // Use direct axios call to ensure proper blob handling
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/quotation-items/sample-excel`, {
        responseType: 'blob',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });
      
      // Handle blob response
      const blob = response.data;
      
      // Check if blob is actually an error response (small size might indicate JSON error)
      if (blob.size < 100) {
        const text = await blob.text();
        try {
          const errorData = JSON.parse(text);
          toast.error(errorData.error || errorData.message || 'Failed to download sample Excel file');
          return;
        } catch {
          // Not JSON, might be actual small file, continue with download
        }
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'quotation-items-template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Sample Excel file downloaded successfully');
    } catch (error: any) {
      console.error('Error downloading sample Excel:', error);
      
      // Handle blob error responses
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          toast.error(errorData.error || errorData.message || 'Failed to download sample Excel file');
        } catch {
          toast.error('Failed to download sample Excel file. Please check your connection and try again.');
        }
      } else if (error.response?.data) {
        const errorMsg = error.response.data.error || error.response.data.message || 'Failed to download sample Excel file';
        toast.error(errorMsg);
      } else if (error.message) {
        toast.error(`Failed to download sample Excel file: ${error.message}`);
      } else {
        toast.error('Failed to download sample Excel file. Please check your connection and try again.');
      }
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchItems(),
          fetchSchedules(),
          fetchSections(),
          fetchCategories(),
          fetchBrands(),
          fetchTypes(),
          fetchCompressorTypes(),
          fetchCountries(),
          fetchUnits(),
        ]);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenModal = (item?: QuotationItem) => {
    if (item) {
      const id = item._id;
      openingItemIdRef.current = id;
      setEditingItem(item);
      setFormData(itemToFormData(item));
      setModalOpen(true);
      apiService.getQuotationItem(id).then((res) => {
        const data = res.data;
        if (openingItemIdRef.current === data._id) {
          setFormData(itemToFormData(data));
        }
      }).catch(() => {
        // Keep formData from list if fetch fails
      });
    } else {
      openingItemIdRef.current = null;
      setEditingItem(null);
      setFormData({
        name: '',
        schedules: [],
        sections: [],
        parentCategory: '',
        subCategory: '',
        brand: '',
        model: '',
        sku: '',
        type: '',
        compressorType: '',
        countryOfOrigin: '',
        countryOfAssembly: '',
        details: '',
        description: '',
        specifications: '',
        coolingCapacity: '',
        serialNo: '',
        partNo: '',
        remarks: '',
        purchasePrice: '',
        units: [],
      });
      setModalOpen(true);
    }
  };

  const handleOpenViewModal = (item: QuotationItem) => {
    const id = item._id;
    openingItemIdRef.current = id;
    setEditingItem(null);
    setViewingItem(item);
    setFormData(itemToFormData(item));
    setModalOpen(true);
    apiService.getQuotationItem(id).then((res) => {
      const data = res.data;
      if (openingItemIdRef.current === data._id) {
        setFormData(itemToFormData(data));
      }
    }).catch(() => {
      // Keep formData from list if fetch fails
    });
  };

  const handleCloseModal = () => {
    openingItemIdRef.current = null;
    setUnitsDropdownOpen(false);
    setSchedulesDropdownOpen(false);
    setSectionsDropdownOpen(false);
    setModalOpen(false);
    setEditingItem(null);
    setViewingItem(null);
    setFormData({
      name: '',
      schedules: [],
      sections: [],
      parentCategory: '',
      subCategory: '',
      brand: '',
      model: '',
      sku: '',
      type: '',
      compressorType: '',
      countryOfOrigin: '',
      countryOfAssembly: '',
      details: '',
      description: '',
      specifications: '',
      coolingCapacity: '',
      serialNo: '',
      partNo: '',
      remarks: '',
      purchasePrice: '',
      units: [],
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (viewingItem) return;

    if (!formData.name.trim()) {
      toast.error('Item name is required');
      return;
    }

    if (!formData.purchasePrice || isNaN(parseFloat(formData.purchasePrice))) {
      toast.error('Valid purchase price is required');
      return;
    }

    try {
      setSubmitting(true);
      const payload: any = {
        name: formData.name,
        purchasePrice: parseFloat(formData.purchasePrice),
        unitPrice: parseFloat(formData.purchasePrice), // Sync with purchasePrice
      };

      // Add optional fields only if they have values
      if (formData.schedules.length > 0) payload.schedules = formData.schedules;
      if (formData.sections.length > 0) payload.sections = formData.sections;
      // Always send category refs so backend can store or clear them
      payload.parentCategory = formData.parentCategory || null;
      payload.subCategory = formData.subCategory || null;
      if (formData.brand) payload.brand = formData.brand;
      if (formData.model) payload.model = formData.model;
      if (formData.sku) payload.sku = formData.sku;
      if (formData.type) payload.type = formData.type;
      if (formData.compressorType) payload.compressorType = formData.compressorType;
      if (formData.countryOfOrigin) payload.countryOfOrigin = formData.countryOfOrigin;
      if (formData.countryOfAssembly) payload.countryOfAssembly = formData.countryOfAssembly;
      if (formData.details) payload.details = formData.details;
      if (formData.description) payload.description = formData.description;
      if (formData.specifications) payload.specifications = formData.specifications;
      if (formData.coolingCapacity) payload.coolingCapacity = formData.coolingCapacity;
      if (formData.serialNo) payload.serialNo = formData.serialNo;
      if (formData.partNo) payload.partNo = formData.partNo;
      if (formData.remarks) payload.remarks = formData.remarks;
      if (formData.units.length > 0) {
        payload.units = formData.units;
        payload.unit = formData.units[0];
      }

      if (editingItem) {
        if (!editingItem._id) {
          toast.error('Invalid item ID. Please refresh the page and try again.');
          return;
        }
        await apiService.updateQuotationItem(editingItem._id, payload);
        toast.success('Item updated successfully');
      } else {
        await apiService.createQuotationItem(payload);
        toast.success('Item created successfully');
      }
      handleCloseModal();
      fetchItems();
    } catch (error: any) {
      console.error('Error saving item:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to save item';
      toast.error(errorMessage);
      
      // If item not found, refresh the list
      if (error.response?.status === 404) {
        setTimeout(() => {
          fetchItems();
        }, 1000);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      await apiService.deleteQuotationItem(id);
      toast.success('Item deleted successfully');
      fetchItems();
    } catch (error: any) {
      console.error('Error deleting item:', error);
      toast.error(error.response?.data?.message || 'Failed to delete item');
    }
  };

  const primaryCategories = React.useMemo(() => {
    if (!Array.isArray(categories)) return [];
    return categories.filter((c: any) => {
      const parent = c.parent;
      return !parent || (typeof parent === 'string' && !parent) || (typeof parent === 'object' && !parent?._id);
    });
  }, [categories]);

  const subCategoriesForPrimary = React.useMemo(() => {
    if (!filterPrimaryCategory || !Array.isArray(categories)) return [];
    return categories.filter((c: any) => {
      const parentId = typeof c.parent === 'object' ? c.parent?._id : c.parent;
      return parentId === filterPrimaryCategory;
    });
  }, [categories, filterPrimaryCategory]);

  const filteredItems = items.filter(item => {
    const term = searchTerm.toLowerCase();
    const categoryValue = typeof item.category === 'object' && item.category !== null
      ? (item.category as any).name || String(item.category)
      : item.category || '';
    const matchesSearch = !term || (
      item.name.toLowerCase().includes(term) ||
      item.description?.toLowerCase().includes(term) ||
      categoryValue.toLowerCase().includes(term) ||
      item.sku?.toLowerCase().includes(term)
    );
    if (!matchesSearch) return false;

    const itemParentId = (item as any).parentCategory?._id ?? (item as any).parentCategory;
    const itemSubId = (item as any).subCategory?._id ?? (item as any).subCategory;
    const itemBrandId = (item as any).brand?._id ?? (item as any).brand;

    const matchesPrimary = !filterPrimaryCategory || itemParentId === filterPrimaryCategory;
    const matchesSub = !filterSubCategory || itemSubId === filterSubCategory;
    const matchesBrand = !filterBrand || itemBrandId === filterBrand;

    return matchesSearch && matchesPrimary && matchesSub && matchesBrand;
  });

  const getRefName = (ref: any): string => {
    if (!ref) return '';
    if (typeof ref === 'string') return ref;
    return (ref as any).name ?? String(ref);
  };

  const getUnitDisplay = (unit: any): string => {
    if (!unit) return '-';
    if (typeof unit === 'object' && unit !== null) return (unit as any).symbol || (unit as any).name || '';
    return String(unit);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
            Catalog <span className="text-lg font-normal text-gray-500 ml-2">({filteredItems.length} items)</span>
          </h1>
          <p className="mt-2 text-sm text-gray-600" style={{ fontFamily: 'var(--font-family)' }}>
            Manage products and services for quotations
          </p>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              icon={<Plus className="w-4 h-4" />}
              onClick={() => handleOpenModal()}
            >
              Add Item
            </Button>
            <Button
              type="button"
              variant="outline"
              icon={<Download className="w-4 h-4" />}
              onClick={handleDownloadSample}
            >
              Download Sample
            </Button>
            <Button
              type="button"
              variant="outline"
              icon={<Upload className="w-4 h-4" />}
              onClick={handleImportClick}
              loading={importing}
            >
              Import from Excel
            </Button>
          </div>
        </motion.div>
      </motion.div>

      {/* Hidden file input for Excel import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card padding="md">
          <Input
            placeholder="Search items by name, description, category, or SKU..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<Search className="w-5 h-5" />}
            fullWidth
          />
        </Card>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
      >
        <Card padding="md">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              icon={<Filter className="w-4 h-4" />}
              onClick={() => setFilterPanelOpen((prev) => !prev)}
              className={filterPanelOpen ? 'bg-gray-100 border-gray-300' : ''}
            >
              Filter
            </Button>
          </div>
          {filterPanelOpen && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1.5">Primary Category</label>
                  <select
                    value={filterPrimaryCategory}
                    onChange={(e) => {
                      setFilterPrimaryCategory(e.target.value);
                      setFilterSubCategory('');
                    }}
                    className="block w-full rounded-lg border border-gray-200 bg-white py-2.5 px-3.5 text-sm font-medium text-gray-700 shadow-sm transition-colors outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 hover:border-gray-300"
                  >
                    <option value="">All</option>
                    {primaryCategories.map((c: any) => (
                      <option key={c._id} value={c._id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1.5">Sub Category</label>
                  <select
                    value={filterSubCategory}
                    onChange={(e) => setFilterSubCategory(e.target.value)}
                    disabled={!filterPrimaryCategory}
                    className="block w-full rounded-lg border border-gray-200 bg-white py-2.5 px-3.5 text-sm font-medium text-gray-700 shadow-sm transition-colors outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 hover:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    <option value="">All</option>
                    {subCategoriesForPrimary.map((c: any) => (
                      <option key={c._id} value={c._id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1.5">Brand</label>
                  <select
                    value={filterBrand}
                    onChange={(e) => setFilterBrand(e.target.value)}
                    className="block w-full rounded-lg border border-gray-200 bg-white py-2.5 px-3.5 text-sm font-medium text-gray-700 shadow-sm transition-colors outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 hover:border-gray-300"
                  >
                    <option value="">All</option>
                    {Array.isArray(brands) && brands.map((b: any) => (
                      <option key={b._id} value={b._id}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFilterPrimaryCategory('');
                      setFilterSubCategory('');
                      setFilterBrand('');
                    }}
                  >
                    Clear filters
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </motion.div>

      {/* Items Card View */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        {loading ? (
          <Card padding="lg">
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          </Card>
        ) : filteredItems.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredItems.map((row, index) => {
              const categoryDisplay = typeof row.category === 'object' && row.category !== null
                ? (row.category as any).name || String(row.category)
                : row.category;
              const parentCat = getRefName((row as any).parentCategory);
              const subCat = getRefName((row as any).subCategory);
              const categoryText = [parentCat, subCat].filter(Boolean).join(' / ') || categoryDisplay || '-';
              const detailSnippet = [row.details, row.description, row.specifications].filter(Boolean).join(' ').slice(0, 120);
              return (
                <motion.div
                  key={row._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                >
                  <Card padding="md" className="h-full flex flex-col">
                    <div className="flex justify-between items-start gap-2 mb-3">
                      <h3 className="text-base font-semibold text-gray-900 line-clamp-2" style={{ fontFamily: 'var(--font-family)' }}>
                        {row.name}
                      </h3>
                      <div className="flex gap-0.5 shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-1.5"
                          icon={<Eye className="w-4 h-4" />}
                          onClick={() => handleOpenViewModal(row)}
                          title="View"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 p-1.5"
                          icon={<Edit2 className="w-4 h-4" />}
                          onClick={() => handleOpenModal(row)}
                          title="Edit"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 p-1.5"
                          icon={<DollarSign className="w-4 h-4" />}
                          onClick={() => setPriceUpdateItem(row)}
                          title="Update purchase price"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1.5"
                          icon={<Trash2 className="w-4 h-4" />}
                          onClick={() => handleDelete(row._id)}
                          title="Delete"
                        />
                      </div>
                    </div>
                    {row.description && (
                      <p className="text-sm text-gray-500 line-clamp-2 mb-3" style={{ fontFamily: 'var(--font-family)' }}>
                        {row.description}
                      </p>
                    )}
                    <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm flex-1">
                      {row.sku && (
                        <>
                          <dt className="text-gray-500">SKU</dt>
                          <dd className="text-gray-900 font-medium">{row.sku}</dd>
                        </>
                      )}
                      {getRefName((row as any).brand) && (
                        <>
                          <dt className="text-gray-500">Brand</dt>
                          <dd className="text-gray-900">{getRefName((row as any).brand)}</dd>
                        </>
                      )}
                      {row.model && (
                        <>
                          <dt className="text-gray-500">Model</dt>
                          <dd className="text-gray-900 truncate" title={row.model}>{row.model}</dd>
                        </>
                      )}
                      <dt className="text-gray-500">Category</dt>
                      <dd className="text-gray-900">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {categoryText}
                        </span>
                      </dd>
                      {getRefName((row as any).type) && (
                        <>
                          <dt className="text-gray-500">Type</dt>
                          <dd className="text-gray-900">{getRefName((row as any).type)}</dd>
                        </>
                      )}
                      {getRefName((row as any).compressorType) && (
                        <>
                          <dt className="text-gray-500">Compressor</dt>
                          <dd className="text-gray-900">{getRefName((row as any).compressorType)}</dd>
                        </>
                      )}
                      {(getRefName((row as any).countryOfOrigin) || getRefName((row as any).countryOfAssembly)) && (
                        <>
                          <dt className="text-gray-500">Origin / Assembly</dt>
                          <dd className="text-gray-900">
                            {[getRefName((row as any).countryOfOrigin), getRefName((row as any).countryOfAssembly)].filter(Boolean).join(' / ') || '-'}
                          </dd>
                        </>
                      )}
                      <dt className="text-gray-500">Unit(s)</dt>
                      <dd className="text-gray-900">
                        {Array.isArray((row as any).units) && (row as any).units.length > 0
                          ? (row as any).units.map((u: any) => getUnitDisplay(u)).join(', ')
                          : getUnitDisplay((row as any).unit)}
                      </dd>
                      <dt className="text-gray-500">Unit Price</dt>
                      <dd className="text-gray-900 font-semibold text-green-700">
                        {settings?.currencySymbol || '$'}{((row as any).purchasePrice ?? row.unitPrice)?.toLocaleString() ?? '-'}
                      </dd>
                    </dl>
                    {detailSnippet && (
                      <p className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-400 line-clamp-2" style={{ fontFamily: 'var(--font-family)' }}>
                        {detailSnippet}{detailSnippet.length >= 120 ? '…' : ''}
                      </p>
                    )}
                  </Card>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <Card padding="lg">
            <div className="px-6 py-12 text-center">
              <PackageOpen className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
                No catalog items yet
              </h3>
              <p className="mt-1 text-sm text-gray-500" style={{ fontFamily: 'var(--font-family)' }}>
                Get started by adding your first product or service.
              </p>
              <div className="mt-6">
                <Button
                  icon={<Plus className="w-4 h-4" />}
                  onClick={() => handleOpenModal()}
                >
                  Add Item
                </Button>
              </div>
            </div>
          </Card>
        )}
      </motion.div>

      <CatalogItemPriceUpdateModal
        isOpen={!!priceUpdateItem}
        onClose={() => setPriceUpdateItem(null)}
        item={priceUpdateItem}
        onSaved={fetchItems}
      />

      {/* Add/Edit Item Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={viewingItem ? 'View Catalog Item' : editingItem ? 'Edit Catalog Item' : 'Add New Item'}
        size="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {/* Item Name */}
          <Input
            label="Item Name *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            disabled={!!viewingItem}
            placeholder="Enter item name"
            required
            fullWidth
          />

          {/* Schedule & Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div ref={schedulesDropdownRef} className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Schedules <span className="text-gray-500">(Optional, tick one or more)</span>
              </label>
              <button
                type="button"
                onClick={() => !viewingItem && setSchedulesDropdownOpen((prev) => !prev)}
                disabled={!!viewingItem}
                className="flex items-center justify-between w-full rounded-lg border border-gray-300 bg-white py-2.5 px-3.5 text-left text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                style={{ fontFamily: 'var(--font-family)' }}
              >
                <span className="truncate">
                  {formData.schedules.length === 0
                    ? 'Select schedules...'
                    : formData.schedules
                        .map((id) => schedules.find((x) => x._id === id)?.name ?? id)
                        .join(', ')}
                </span>
                <ChevronDown className={`ml-2 h-4 w-4 shrink-0 text-gray-500 transition-transform ${schedulesDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {schedulesDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white py-1 shadow-lg max-h-48 overflow-y-auto">
                  {Array.isArray(schedules) && schedules.length > 0 ? (
                    schedules.map((schedule) => {
                      const checked = formData.schedules.includes(schedule._id);
                      return (
                        <label
                          key={schedule._id}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50 select-none"
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            checked={checked}
                            disabled={!!viewingItem}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setFormData((prev) => {
                                const current = new Set(prev.schedules);
                                if (isChecked) {
                                  current.add(schedule._id);
                                } else {
                                  current.delete(schedule._id);
                                }
                                return { ...prev, schedules: Array.from(current), sections: [] };
                              });
                            }}
                          />
                          <span>{schedule.name}</span>
                        </label>
                      );
                    })
                  ) : (
                    <p className="px-3 py-2 text-xs text-gray-500">No schedules available. Create schedules first.</p>
                  )}
                </div>
              )}
            </div>

            <div ref={sectionsDropdownRef} className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sections <span className="text-gray-500">(Optional, tick one or more)</span>
              </label>
              {(() => {
                const filteredSections = Array.isArray(sections) && formData.schedules.length > 0
                  ? sections.filter((section) => {
                      const sectionScheduleIds = Array.isArray(section.schedules)
                        ? section.schedules.map((s: any) => s?._id || s).filter(Boolean)
                        : [];
                      return formData.schedules.some((selectedScheduleId) =>
                        sectionScheduleIds.includes(selectedScheduleId)
                      );
                    })
                  : formData.schedules.length === 0
                  ? []
                  : Array.isArray(sections) ? sections : [];
                const sectionLabel =
                  formData.sections.length === 0
                    ? 'Select sections...'
                    : formData.sections
                        .map((id) => sections.find((x) => x._id === id)?.name ?? id)
                        .join(', ');
                return (
                  <>
                    <button
                      type="button"
                      onClick={() => !viewingItem && setSectionsDropdownOpen((prev) => !prev)}
                      disabled={!!viewingItem}
                      className="flex items-center justify-between w-full rounded-lg border border-gray-300 bg-white py-2.5 px-3.5 text-left text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                      style={{ fontFamily: 'var(--font-family)' }}
                    >
                      <span className="truncate">{sectionLabel}</span>
                      <ChevronDown className={`ml-2 h-4 w-4 shrink-0 text-gray-500 transition-transform ${sectionsDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {sectionsDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white py-1 shadow-lg max-h-48 overflow-y-auto">
                        {filteredSections.length > 0 ? (
                          filteredSections.map((section) => {
                            const checked = formData.sections.includes(section._id);
                            return (
                              <label
                                key={section._id}
                                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50 select-none"
                              >
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                  checked={checked}
                                  disabled={!!viewingItem}
                                  onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    setFormData((prev) => {
                                      const current = new Set(prev.sections);
                                      if (isChecked) {
                                        current.add(section._id);
                                      } else {
                                        current.delete(section._id);
                                      }
                                      return { ...prev, sections: Array.from(current) };
                                    });
                                  }}
                                />
                                <span>{section.name}</span>
                              </label>
                            );
                          })
                        ) : formData.schedules.length === 0 ? (
                          <p className="px-3 py-2 text-xs text-gray-500">Select schedules first to see available sections.</p>
                        ) : (
                          <p className="px-3 py-2 text-xs text-gray-500">No sections available for selected schedules.</p>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          {/* Categories */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parent Category</label>
              <select
                value={formData.parentCategory}
                onChange={(e) => setFormData({ ...formData, parentCategory: e.target.value, subCategory: '' })}
                className="form-select"
                disabled={!!viewingItem}
              >
                <option value="">Select Parent Category</option>
                {Array.isArray(categories) && categories
                  .filter((category) => {
                    // Only show categories with no parent (root categories)
                    return !category.parent || 
                           (typeof category.parent === 'string' && !category.parent) ||
                           (typeof category.parent === 'object' && !category.parent._id);
                  })
                  .map((category) => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sub Category</label>
              <select
                value={formData.subCategory}
                onChange={(e) => setFormData({ ...formData, subCategory: e.target.value })}
                className="form-select"
                disabled={!!viewingItem || !formData.parentCategory}
              >
                <option value="">{formData.parentCategory ? 'Select Sub Category' : 'Select Parent Category first'}</option>
                {formData.parentCategory && Array.isArray(categories) && categories
                  .filter((category) => {
                    // Only show categories that have the selected parent category
                    const parentId = typeof category.parent === 'object' 
                      ? category.parent?._id 
                      : category.parent;
                    return parentId === formData.parentCategory;
                  })
                  .map((category) => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Brand & Model */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
              <select
                value={formData.brand}
                onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                className="form-select"
                disabled={!!viewingItem}
              >
                <option value="">Select Brand</option>
                {Array.isArray(brands) && brands.map((brand) => (
                  <option key={brand._id} value={brand._id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Model"
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              placeholder="Enter model"
              fullWidth
              disabled={!!viewingItem}
            />
          </div>

          {/* SKU & Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="SKU"
              value={formData.sku}
              onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
              placeholder="Enter SKU code"
              fullWidth
              disabled={!!viewingItem}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="form-select"
                disabled={!!viewingItem}
              >
                <option value="">Select Type</option>
                {Array.isArray(types) && types.map((type) => (
                  <option key={type._id} value={type._id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Compressor Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Compressor Type</label>
            <select
              value={formData.compressorType}
              onChange={(e) => setFormData({ ...formData, compressorType: e.target.value })}
              className="form-select"
              disabled={!!viewingItem}
            >
              <option value="">Select Compressor Type</option>
              {Array.isArray(compressorTypes) && compressorTypes.map((ct) => (
                <option key={ct._id} value={ct._id}>
                  {ct.name}
                </option>
              ))}
            </select>
          </div>

          {/* Countries */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country of Origin</label>
              <select
                value={formData.countryOfOrigin}
                onChange={(e) => setFormData({ ...formData, countryOfOrigin: e.target.value })}
                className="form-select"
                disabled={!!viewingItem}
              >
                <option value="">Select Country</option>
                {Array.isArray(countries) && countries.map((country) => (
                  <option key={country._id} value={country._id}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country of Assembly</label>
              <select
                value={formData.countryOfAssembly}
                onChange={(e) => setFormData({ ...formData, countryOfAssembly: e.target.value })}
                className="form-select"
                disabled={!!viewingItem}
              >
                <option value="">Select Country</option>
                {Array.isArray(countries) && countries.map((country) => (
                  <option key={country._id} value={country._id}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Details */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
            <textarea
              className="form-textarea"
              rows={3}
              value={formData.details}
              onChange={(e) => setFormData({ ...formData, details: e.target.value })}
              placeholder="Enter item details"
              disabled={!!viewingItem}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              className="form-textarea"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter item description"
              disabled={!!viewingItem}
            />
          </div>

          {/* Specifications */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Specifications</label>
            <textarea
              className="form-textarea"
              rows={3}
              value={formData.specifications}
              onChange={(e) => setFormData({ ...formData, specifications: e.target.value })}
              placeholder="Enter item specifications"
              disabled={!!viewingItem}
            />
          </div>

          {/* Cooling Capacity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cooling Capacity</label>
            <Input
              value={formData.coolingCapacity}
              onChange={(e) => setFormData({ ...formData, coolingCapacity: e.target.value })}
              placeholder="e.g., 12000 BTU, 1.5 Ton"
              fullWidth
              disabled={!!viewingItem}
            />
          </div>

          {/* Serial No & Part No */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Serial No."
              value={formData.serialNo}
              onChange={(e) => setFormData({ ...formData, serialNo: e.target.value })}
              placeholder="Enter serial number"
              fullWidth
              disabled={!!viewingItem}
            />

            <Input
              label="Part No."
              value={formData.partNo}
              onChange={(e) => setFormData({ ...formData, partNo: e.target.value })}
              placeholder="Enter part number"
              fullWidth
              disabled={!!viewingItem}
            />
          </div>

          {/* Remarks (user notes only; not shown in BOQ/Quotation) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remarks <span className="text-gray-500 font-normal">(user notes only, not included in BOQ or Quotation view/print)</span>
            </label>
            <textarea
              className="form-textarea w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:bg-gray-100 disabled:text-gray-400"
              rows={3}
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              placeholder="Internal notes (not shown on BOQ or Quotation)"
              disabled={!!viewingItem}
            />
          </div>

          {/* Purchase Price & Units */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Purchase Price *"
              type="number"
              step="0.01"
              value={formData.purchasePrice}
              onChange={(e) => setFormData({ ...formData, purchasePrice: e.target.value })}
              placeholder="0.00"
              required
              fullWidth
              disabled={!!viewingItem}
            />

            <div ref={unitsDropdownRef} className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Units <span className="text-gray-500">(Optional, tick one or more)</span>
              </label>
              <button
                type="button"
                onClick={() => !viewingItem && setUnitsDropdownOpen((prev) => !prev)}
                disabled={!!viewingItem}
                className="flex items-center justify-between w-full rounded-lg border border-gray-300 bg-white py-2.5 px-3.5 text-left text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                style={{ fontFamily: 'var(--font-family)' }}
              >
                <span className="truncate">
                  {formData.units.length === 0
                    ? 'Select units...'
                    : formData.units
                        .map((id) => {
                          const u = Array.isArray(units) ? units.find((x) => x._id === id) : null;
                          return u ? (u.symbol || u.name || u._id) : id;
                        })
                        .join(', ')}
                </span>
                <ChevronDown className={`ml-2 h-4 w-4 shrink-0 text-gray-500 transition-transform ${unitsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {unitsDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white py-1 shadow-lg max-h-48 overflow-y-auto">
                  {Array.isArray(units) && units.length > 0 ? (
                    units.map((unit) => {
                      const checked = formData.units.includes(unit._id);
                      const label = (unit as any).symbol || (unit as any).name || unit._id;
                      return (
                        <label
                          key={unit._id}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50 select-none"
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            checked={checked}
                            disabled={!!viewingItem}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setFormData((prev) => {
                                const current = new Set(prev.units);
                                if (isChecked) {
                                  current.add(unit._id);
                                } else {
                                  current.delete(unit._id);
                                }
                                return { ...prev, units: Array.from(current) };
                              });
                            }}
                          />
                          <span>{label}</span>
                        </label>
                      );
                    })
                  ) : (
                    <p className="px-3 py-2 text-xs text-gray-500">No units available. Create units first.</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t sticky bottom-0 bg-white">
            {viewingItem ? (
              <Button type="button" onClick={handleCloseModal}>
                Close
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={submitting}
                >
                  {editingItem ? 'Update Item' : 'Create Item'}
                </Button>
              </>
            )}
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default QuotationItemsPage;
